import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiChevronDown, FiChevronRight, FiCheck, FiSearch } from 'react-icons/fi';
import { apiService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { buildLabSearchText, matchesLabSearch, normalizeLabSearch as normalize } from './labSearch';
import './LabReports.css';

export { matchesLabSearch } from './labSearch';

const clinicalLabels = {
    laboratory: { en: 'Laboratory tests', ru: 'Лабораторные исследования', uz: 'Laboratoriya tahlillari' },
    imaging: { en: 'Imaging', ru: 'Инструментальные исследования', uz: 'Instrumental tekshiruvlar' },
    dynamic: { en: 'Dynamic testing', ru: 'Динамические пробы', uz: 'Dinamik tekshiruvlar' },
    urinalysis: { en: 'Urinalysis', ru: 'Анализ мочи', uz: 'Siydik tahlili' },
    biochemistry: { en: 'Biochemistry', ru: 'Биохимия', uz: 'Biokimyo' },
    glycemia: { en: 'Glycemia', ru: 'Гликемия', uz: 'Glikemiya' },
    electrolytes: { en: 'Electrolytes', ru: 'Электролиты', uz: 'Elektrolitlar' },
    renal: { en: 'Renal function', ru: 'Функция почек', uz: 'Buyrak faoliyati' },
    liver: { en: 'Liver function', ru: 'Функция печени', uz: 'Jigar faoliyati' },
    lipids: { en: 'Lipid profile', ru: 'Липидный профиль', uz: 'Lipid profili' },
    fbc: { en: 'Full blood count', ru: 'Общий анализ крови', uz: 'Umumiy qon tahlili' },
    glucose: { en: 'Glucose', ru: 'Глюкоза', uz: 'Glyukoza' },
    capillary_glucose: { en: 'Capillary glucose', ru: 'Капиллярная глюкоза', uz: 'Kapillyar glyukoza' },
    hba1c: { en: 'HbA1c', ru: 'HbA1c', uz: 'HbA1c' },
    ketones: { en: 'Ketones', ru: 'Кетоны', uz: 'Ketonlar' },
    blood_ketones: { en: 'Blood ketones', ru: 'Кетоны крови', uz: 'Qon ketonlari' },
    protein: { en: 'Protein', ru: 'Белок', uz: 'Oqsil' },
    nitrites: { en: 'Nitrites', ru: 'Нитриты', uz: 'Nitritlar' },
    potassium: { en: 'Potassium', ru: 'Калий', uz: 'Kaliy' },
    sodium: { en: 'Sodium', ru: 'Натрий', uz: 'Natriy' },
    urea: { en: 'Urea', ru: 'Мочевина', uz: 'Mochevina' },
    creatinine: { en: 'Creatinine', ru: 'Креатинин', uz: 'Kreatinin' },
    haemoglobin: { en: 'Haemoglobin', ru: 'Гемоглобин', uz: 'Gemoglobin' },
    wbc: { en: 'White blood cells', ru: 'Лейкоциты', uz: 'Leykotsitlar' },
    platelets: { en: 'Platelets', ru: 'Тромбоциты', uz: 'Trombotsitlar' },
    ecg: { en: 'ECG', ru: 'ЭКГ', uz: 'EKG' },
    chest_xray: { en: 'Chest X-ray', ru: 'Рентген грудной клетки', uz: 'Ko‘krak qafasi rentgeni' },
    ultrasound: { en: 'Ultrasound', ru: 'УЗИ', uz: 'UTT' },
    ct_mri: { en: 'CT / MRI', ru: 'КТ / МРТ', uz: 'KT / MRT' },
    antibodies: { en: 'Antibodies', ru: 'Антитела', uz: 'Antitanalar' },
    gad_antibodies: { en: 'GAD antibodies', ru: 'Антитела GAD', uz: 'GAD antitanalari' },
    anion_gap: { en: 'Anion gap', ru: 'Анионная разница', uz: 'Anion oralig‘i' },
    abg_ph: { en: 'Arterial blood gas pH', ru: 'pH артериальной крови', uz: 'Arterial qon pH ko‘rsatkichi' }
};

const labelAliases = {
    laboratory: ['lab reports', 'laboratory tests', 'laboratory', 'анализы', 'лабораторные исследования', 'tahlillar', 'laboratoriya'],
    imaging: ['imaging', 'instrumental studies', 'инструментальные', 'инструментальные исследования', 'instrumental tekshiruvlar'],
    dynamic: ['dynamic testing', 'dynamic_testing', 'динамические пробы', 'dinamik tekshiruvlar'],
    urinalysis: ['urinalysis', 'urine', 'ur', 'ua', 'анализ мочи', 'моча', 'siydik tahlili', 'siydik'], biochemistry: ['biochemistry', 'биохимия', 'biokimyo'],
    glycemia: ['glycemia', 'гликемия', 'glikemiya'], electrolytes: ['electrolytes', 'электролиты', 'elektrolitlar'],
    renal: ['renal function', 'renal_function', 'почки и печень', 'функция почек', 'buyrak faoliyati'],
    liver: ['liver function', 'liver_function', 'функция печени', 'jigar faoliyati'],
    lipids: ['lipids', 'lipid profile', 'lipid_profile', 'липидный профиль', 'lipid profili'],
    fbc: ['fbc', 'оак', 'full blood count', 'общий анализ крови', 'umumiy qon tahlili'],
    glucose: ['glucose', 'глюкоза', 'glyukoza'], capillary_glucose: ['capillary glucose', 'capillary_glucose', 'капиллярная глюкоза', 'kapillyar glyukoza'],
    hba1c: ['hba1c', 'гликированный гемоглобин', 'glikirlangan gemoglobin'], ketones: ['ketones', 'ketonlar', 'кетоны', 'ацетон'],
    blood_ketones: ['blood ketones', 'blood_ketones', 'кетоны крови', 'qon ketonlari'], protein: ['protein', 'белок', 'oqsil'],
    nitrites: ['nitrites', 'нитриты', 'nitritlar'], potassium: ['potassium', 'калий', 'kaliy'], sodium: ['sodium', 'натрий', 'natriy'],
    urea: ['urea', 'мочевина', 'mochevina'], creatinine: ['creatinine', 'креатинин', 'kreatinin'],
    haemoglobin: ['haemoglobin', 'hemoglobin', 'гемоглобин', 'gemoglobin', 'hb'], wbc: ['wbc', 'white blood cells', 'лейкоциты', 'leykotsitlar'],
    platelets: ['platelets', 'тромбоциты', 'trombotsitlar'], ecg: ['ecg', 'ekg', 'экг', 'кардиограмма'],
    chest_xray: ['chest xray', 'chest x ray', 'chest_xray', 'рентген грудной клетки', 'ko krak qafasi rentgeni'],
    ultrasound: ['ultrasound', 'узи', 'utt'], ct_mri: ['ct mri', 'ct_mri', 'кт', 'мрт', 'kt', 'mrt'],
    antibodies: ['antibodies', 'антитела', 'antitanalar'], gad_antibodies: ['gad antibodies', 'gad_antibodies', 'антитела gad', 'gad antitanalari'],
    anion_gap: ['anion gap', 'anion_gap', 'анионная разница', 'анионный промежуток', 'anion oraligi'],
    abg_ph: ['abg ph', 'abg_ph', 'blood gas ph', 'газы крови', 'кщс', 'qon gazlari', 'qon ph']
};

const labelKey = (value) => {
    const normalized = normalize(value);
    return Object.keys(labelAliases).find(key => normalize(key) === normalized || labelAliases[key].some(alias => normalize(alias) === normalized));
};
const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
const translateLabel = (value, language) => {
    const key = labelKey(value);
    return key ? clinicalLabels[key]?.[language] || clinicalLabels[key]?.en : humanize(value);
};

const unavailablePattern = /^(not available|not specified|not done|unavailable|n\/?a|не\s?доступ(?:ен|на|но|ны)?|не делалось|не выполнено|не указан(?:о|а|ы)?|нет результата|mavjud emas|bajarilmagan|ko['‘’ʻʼ]?rsatilmagan)[.!]?$/i;
const isAvailable = (value) => value !== null && value !== undefined && String(value).trim() !== '' && !unavailablePattern.test(String(value).trim());
const unknownLabelNeedsTranslation = (value, language) => {
    if (labelKey(value)) return false;
    const hasCyrillic = /\p{Script=Cyrillic}/u.test(String(value || ''));
    if (language === 'en') return hasCyrillic;
    if (language === 'ru') return !hasCyrillic;
    return true;
};
const resultReplacements = {
    en: [[/Глюкоза мочи/gi, 'Urine glucose'], [/Ацетон в моче/gi, 'Urine ketones'], [/положительный/gi, 'positive'], [/при поступлении/gi, 'on admission'], [/Мочевина/gi, 'Urea'], [/Креатинин/gi, 'Creatinine'], [/Тромбоциты/gi, 'Platelets'], [/Синусовая тахикардия/gi, 'Sinus tachycardia'], [/с ЧСС/gi, 'with heart rate'], [/диффузно-дистрофические изменения/gi, 'diffuse dystrophic changes'], [/Норма/gi, 'Normal'], [/Положительно/gi, 'Positive']],
    uz: [[/Глюкоза мочи/gi, 'Siydik glyukozasi'], [/Ацетон в моче/gi, 'Siydik ketonlari'], [/положительный/gi, 'musbat'], [/при поступлении/gi, 'qabul vaqtida'], [/Мочевина/gi, 'Mochevina'], [/Креатинин/gi, 'Kreatinin'], [/Тромбоциты/gi, 'Trombotsitlar'], [/Синусовая тахикардия/gi, 'Sinusli taxikardiya'], [/с ЧСС/gi, 'yurak urish tezligi'], [/диффузно-дистрофические изменения/gi, 'diffuz distrofik o‘zgarishlar'], [/Норма/gi, 'Me’yorida'], [/Положительно/gi, 'Musbat']],
    ru: [[/Urine glucose/gi, 'Глюкоза мочи'], [/Urine ketones/gi, 'Кетоны мочи'], [/Positive/gi, 'Положительно'], [/Elevated/gi, 'Повышено'], [/High/gi, 'Высокий уровень'], [/Normal/gi, 'Норма']]
};
const translateResult = (value, language) => {
    if (!isAvailable(value)) return { en: 'Not available', ru: 'Нет результата', uz: 'Natija mavjud emas' }[language];
    let output = String(value);
    (resultReplacements[language] || []).forEach(([pattern, replacement]) => { output = output.replace(pattern, replacement); });
    return output;
};

const reportValueText = value => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.map(reportValueText).join('; ');
    if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${humanize(key)}: ${reportValueText(item)}`).join('; ');
    return String(value);
};

// Clinical flags must be authored. Searching narrative substrings can mark
// "swallow", "flow", or "not elevated" as abnormal and cannot establish normality.
const reportFlag = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
    if (typeof value.abnormal === 'boolean') return value.abnormal ? 'abnormal' : 'normal';
    const flag = String(value.flag || '').trim().toLowerCase();
    if (['h', 'hh', 'l', 'll', 'high', 'low', 'abnormal'].includes(flag)) return 'abnormal';
    return ['n', 'normal'].includes(flag) ? 'normal' : '';
};

const flattenTests = (value, prefix = '', path = []) => {
    const leaf = (result, name = prefix, sourceValue = value, availabilityValue = result) => ({
        id: JSON.stringify(path), name: name || 'Result', result, sourceValue, availabilityValue
    });
    if (Array.isArray(value)) {
        if (!value.length) return [];
        if (value.every(item => item === null || typeof item !== 'object')) return [leaf(value.join('; '))];
        return value.flatMap((item, index) => {
            const named = item && typeof item === 'object' && ['name', 'test_name', 'test', 'label'].some(key => typeof item[key] === 'string');
            return flattenTests(item, named ? prefix : prefix ? `${prefix} / ${index + 1}` : String(index + 1), [...path, index]);
        });
    }
    if (value === null || value === undefined || typeof value !== 'object') return [leaf(value)];
    const nameKey = ['name', 'test_name', 'test', 'label'].find(key => typeof value[key] === 'string');
    const resultKey = ['result', 'value'].find(key => Object.prototype.hasOwnProperty.call(value, key));
    if (resultKey) {
        const result = value[resultKey];
        const name = nameKey ? (prefix ? `${prefix} / ${value[nameKey]}` : value[nameKey]) : prefix || String(path[path.length - 1] || 'Result');
        const details = Object.entries(value).filter(([key]) => key !== nameKey && key !== resultKey && key !== 'id');
        const display = isAvailable(result) ? [reportValueText(result), ...details.map(([key, detail]) => `${humanize(key)}: ${reportValueText(detail)}`)].join('; ') : result;
        return [leaf(display, name, value, result)];
    }
    return Object.entries(value).flatMap(([key, nested]) => flattenTests(nested, prefix ? `${prefix} / ${key}` : key, [...path, key]));
};
const reportNamespaces = new Set(['lab_reports', 'laboratory_tests', 'investigations', 'diagnostic_tests', 'instrumental_studies']);
const labTranslationCache = new Map();

const persistedTranslation = (localizations, language, value) => {
    const dictionary = localizations?.[language] || {};
    const source = String(value || '');
    return dictionary[source] || dictionary[source.replace(/_/g, ' ')] || '';
};
const persistedAliases = (localizations, value) => {
    const source = String(value || '');
    const humanized = source.replace(/_/g, ' ');
    return [...new Set(['en', 'ru', 'uz'].flatMap(language => {
        const dictionary = localizations?.[language] || {};
        return [dictionary[source], dictionary[humanized]];
    }).filter(Boolean))];
};

export const normalizeLabReports = (source, language = 'en', localizations = {}) => {
    if (!source || typeof source !== 'object') return [];
    const rawCategories = [];
    const addCategory = (category, value, path) => {
        const tests = flattenTests(value, '', path);
        if (tests.length) rawCategories.push({ id: JSON.stringify(path), category, tests });
    };
    Object.entries(source).forEach(([root, value]) => {
        if (reportNamespaces.has(root) && value && typeof value === 'object' && !Array.isArray(value)) {
            const scalar = {};
            Object.entries(value).forEach(([key, nested]) => {
                if (nested !== null && typeof nested === 'object') addCategory(key, nested, [root, key]);
                else scalar[key] = nested;
            });
            if (Object.keys(scalar).length) addCategory('laboratory', scalar, [root]);
        } else addCategory(root, value, [root]);
    });
    return rawCategories.map(({ id, category, tests }) => ({
        id,
        category,
        label: persistedTranslation(localizations, language, category) || translateLabel(category, language),
        searchAliases: persistedAliases(localizations, category),
        labelNeedsTranslation: !persistedTranslation(localizations, language, category) && unknownLabelNeedsTranslation(category, language),
        tests: tests.map(({ id: testId, name, result, sourceValue, availabilityValue }) => {
            const available = sourceValue?.available !== false && isAvailable(availabilityValue);
            const persistedResult = persistedTranslation(localizations, language, result);
            const displayResult = persistedResult || translateResult(result, language);
            const flag = available ? reportFlag(sourceValue) : '';
            const labelParts = name.split(' / ');
            const translatedParts = labelParts.map(part => persistedTranslation(localizations, language, part) || translateLabel(part, language));
            return { id: testId, sourceValue, name, label: translatedParts.join(' / '), searchAliases: labelParts.flatMap(part => persistedAliases(localizations, part)), resultSearchAliases: [...persistedAliases(localizations, result), ...persistedAliases(localizations, availabilityValue)], labelNeedsTranslation: labelParts.some(part => !persistedTranslation(localizations, language, part) && unknownLabelNeedsTranslation(part, language)), available, result: result == null ? '' : String(result), displayResult, resultPersisted: Boolean(persistedResult), abnormal: flag === 'abnormal', normal: flag === 'normal' };
        })
    }));
};

const containsHumanLanguage = value => /\p{L}{2,}/u.test(String(value || ''));
const resultNeedsTranslation = (value, language) => {
    if (!containsHumanLanguage(value)) return false;
    if (language === 'en') return /\p{Script=Cyrillic}/u.test(String(value));
    return true;
};
const cacheKey = (language, text) => `${language}\u0000${text}`;

const LabReportsPanel = ({ isOpen, onClose, caseData, sessionId }) => {
    const { language, t } = useLanguage();
    const [expandedCategories, setExpandedCategories] = useState({});
    const [expandedTests, setExpandedTests] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [remoteReports, setRemoteReports] = useState(null);
    const [remoteLocalizations, setRemoteLocalizations] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [translatedTexts, setTranslatedTexts] = useState({});

    useEffect(() => {
        setSearchTerm('');
        setExpandedCategories({});
        setExpandedTests({});
        setRemoteReports(null);
        setRemoteLocalizations({});
        setTranslatedTexts({});
        setLoadError(false);
    }, [caseData?.id, sessionId]);
    useEffect(() => {
        if (!isOpen || !caseData?.id) return undefined;
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        setRemoteReports(null);
        setRemoteLocalizations({});
        const request = sessionId
            ? apiService.getCaseLabs(caseData.id, language, { sessionId })
            : apiService.getCaseLabs(caseData.id, language);
        request
            .then(payload => {
                if (cancelled) return;
                if (!payload?.reports || typeof payload.reports !== 'object' || Array.isArray(payload.reports)) {
                    throw new Error('Invalid lab reports response');
                }
                setRemoteReports(payload.reports);
                setRemoteLocalizations(payload?.localizations || {});
            })
            .catch(() => { if (!cancelled) setLoadError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, caseData?.id, sessionId, language, loadAttempt]);

    const labData = useMemo(() => normalizeLabReports(remoteReports, language, remoteLocalizations), [remoteReports, language, remoteLocalizations]);
    useEffect(() => {
        if (!isOpen || !labData.length) return undefined;
        let cancelled = false;
        const candidates = [...new Set(labData.flatMap(category => [
            ...(category.labelNeedsTranslation ? [category.label] : []),
            ...category.tests.flatMap(test => [
                ...(test.labelNeedsTranslation ? [test.label] : []),
                ...(test.available && !test.resultPersisted && resultNeedsTranslation(test.displayResult, language) ? [test.displayResult] : [])
            ])
        ]).filter(Boolean))];
        const cached = {};
        const missing = [];
        candidates.forEach(text => {
            const translated = labTranslationCache.get(cacheKey(language, text));
            if (translated) cached[text] = translated;
            else missing.push(text);
        });
        setTranslatedTexts(cached);
        if (!missing.length) return () => { cancelled = true; };

        apiService.translateTexts(missing, language, { strict: true })
            .then(payload => {
                const translations = Array.isArray(payload?.data) ? payload.data : [];
                if (cancelled || translations.length !== missing.length) return;
                const next = { ...cached };
                missing.forEach((text, index) => {
                    const translated = String(translations[index] || text).trim();
                    labTranslationCache.set(cacheKey(language, text), translated);
                    next[text] = translated;
                });
                setTranslatedTexts(next);
            })
            .catch(error => console.error('Lab result translation failed; using deterministic fallback:', error));
        return () => { cancelled = true; };
    }, [isOpen, labData, language]);

    const localizedLabData = useMemo(() => labData.map(category => ({
        ...category,
        label: translatedTexts[category.label] || category.label,
        tests: category.tests.map(test => {
            const localized = {
                ...test,
                label: translatedTexts[test.label] || test.label,
                displayResult: translatedTexts[test.displayResult] || test.displayResult
            };
            return { ...localized, searchTokens: buildLabSearchText({ ...category, label: translatedTexts[category.label] || category.label }, localized) };
        })
    })), [labData, translatedTexts]);
    const normalizedSearch = normalize(searchTerm);
    const searchReady = normalizedSearch.length >= 2;
    const visibleLabData = !loading && !loadError && searchReady ? localizedLabData.map(category => ({ ...category, tests: category.tests.filter(test => matchesLabSearch(category, test, normalizedSearch)) })).filter(category => category.tests.length) : [];
    if (!isOpen) return null;

    const toggleCategory = category => setExpandedCategories(prev => ({ ...prev, [category]: !(prev[category] ?? true) }));
    const toggleTest = testKey => setExpandedTests(prev => ({ ...prev, [testKey]: !prev[testKey] }));
    return (
        <div className="lab-reports-panel">
            <div className="lab-panel-header"><div><h3>{t('labReports')}</h3><p>{t('labSearchHelp')}</p></div><button className="close-panel-btn" aria-label={t('closeLabs')} onClick={onClose}><FiX size={20} /></button></div>
            <div className="lab-panel-content">
                <div className="lab-search-box"><FiSearch size={18} /><input aria-label={t('labSearchPlaceholder')} value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder={t('labSearchPlaceholder')} /></div>
                {loading && <p className="lab-empty-hint" role="status">{t('loadingLabs')}</p>}
                {!loading && loadError && <div className="lab-empty-hint" role="alert">{t('labLoadError')} <button className="lab-retry-btn" onClick={() => setLoadAttempt(attempt => attempt + 1)}>{t('retryLabs')}</button></div>}
                {!loading && !loadError && !labData.length && <p className="lab-empty-hint" role="status">{t('noLabs')}</p>}
                {!loading && !loadError && labData.length > 0 && !normalizedSearch && <p className="lab-empty-hint">{t('labTypeHint')}</p>}
                {!loading && !loadError && labData.length > 0 && normalizedSearch && !searchReady && <p className="lab-empty-hint">{t('labSearchMore')}</p>}
                {!loading && !loadError && labData.length > 0 && searchReady && visibleLabData.length === 0 && <p className="lab-empty-hint" role="status">{t('noMatchingReport')}</p>}
                {visibleLabData.map(category => {
                    const categoryExpanded = expandedCategories[category.id] ?? true;
                    return <div key={category.id} className="lab-category">
                        <button className="category-header" onClick={() => toggleCategory(category.id)}>{categoryExpanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}<span className="category-name">{category.label}</span><span className="test-count">{category.tests.length}</span></button>
                        {categoryExpanded && <div className="category-tests">{category.tests.map(test => {
                            const testKey = test.id;
                            const isExpanded = expandedTests[testKey];
                            return <div key={testKey} className="test-item">
                                <button className={`test-header ${!test.available ? 'unavailable' : ''}`} onClick={() => test.available && toggleTest(testKey)} disabled={!test.available}>
                                    <div className="test-status">{test.available ? <FiCheck className="status-icon available" size={16} /> : <FiX className="status-icon unavailable" size={16} />}</div><span className="test-name">{test.label}</span>{test.available && (isExpanded ? <FiChevronDown className="expand-icon" size={14} /> : <FiChevronRight className="expand-icon" size={14} />)}
                                </button>
                                {test.available && isExpanded && <div className="test-details"><div className={`result-row ${test.abnormal ? 'abnormal' : test.normal ? 'normal' : ''}`}><span className="result-label">{t('result')}:</span><span className="result-value">{test.displayResult}</span></div></div>}
                            </div>;
                        })}</div>}
                    </div>;
                })}
            </div>
        </div>
    );
};

export default LabReportsPanel;
